import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Notification, NotificationType, Sport } from '@h2h/shared';
import { AppShell, Content } from '../components/ui/AppShell';
import { Placeholder } from '../components/ui/Placeholder';
import { LeagueSwitcher } from '../components/ui/LeagueSwitcher';
import {
  ArrowRight,
  Bell,
  Calendar,
  Check,
  ChevronLeft,
  Clock,
  SoccerBall,
  Swords,
  Target,
  TrendingUp,
  Trophy,
  ChevronDown,
  Users
} from '../components/ui/icons';
import { Avatar } from '../components/ui/bits';
import { useHaptics } from '../hooks/useHaptics';
import { useAuth } from '../context/AuthContext';
import { useLeagues, useNotifications, useMarkNotificationsRead } from '../hooks/useQueries';
import { notificationRouteOrInbox, notificationCtaLabel } from '../lib/notificationRoute';
import { cn } from '../lib/cn';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Short relative age: "2h", "1d", "3w". */
function ago(ms: number, now: number): string {
  const diff = Math.max(0, now - ms);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

function iconFor(type: NotificationType) {
  switch (type) {
    case 'NEW_ROUND':
      return { Icon: Calendar, ring: 'bg-accent-wash text-accent-ink' };
    case 'PROMOTED':
      return { Icon: Trophy, ring: 'bg-accent-wash text-accent-ink' };
    case 'RELEGATED':
      return { Icon: ChevronDown, ring: 'bg-danger/10 text-danger' };
    case 'ROUND_SETTLED':
      return { Icon: Check, ring: 'bg-accent-wash text-accent-ink' };
    case 'EXACT_BONUS':
      return { Icon: Target, ring: 'bg-accent-wash text-accent-ink' };
    case 'SCORER_BONUS':
      return { Icon: SoccerBall, ring: 'bg-accent-wash text-accent-ink' };
    case 'FRIEND_PASSED':
      return { Icon: TrendingUp, ring: 'bg-[#5B6EE1]/14 text-[#4453B8]' };
    case 'PICKS_LOCK_SOON':
    case 'MINI_LEAGUE_REMINDER':
      return { Icon: Clock, ring: 'bg-gold-top/30 text-gold-ink' };
    case 'MATCH_KICKOFF':
    case 'LIVE_GOAL':
    case 'MATCH_RESULT':
      return { Icon: SoccerBall, ring: 'bg-accent-wash text-accent-ink' };
    case 'MINI_LEAGUE_JOIN':
    case 'MINI_LEAGUE_RESULT':
      return { Icon: Users, ring: 'bg-[#5B6EE1]/14 text-[#4453B8]' };
    case 'DUEL_CHALLENGE':
    case 'DUEL_RESULT':
      return { Icon: Swords, ring: 'bg-[#5B6EE1]/14 text-[#4453B8]' };
    case 'ANNOUNCEMENT':
      return { Icon: Bell, ring: 'bg-accent-wash text-accent-ink' };
    default:
      return { Icon: Bell, ring: 'bg-fill text-ink2' };
  }
}

export default function Notifications() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { leagueId } = useParams<{ leagueId: string }>();
  const { uid } = useAuth();
  const { data: notifications, isLoading } = useNotifications(uid);
  const { data: leagues } = useLeagues();
  const markRead = useMarkNotificationsRead(uid);
  const now = Date.now();
  const sport = leagues?.find((l) => l.leagueId === leagueId)?.sport;

  // This bell is scoped to the league world you're inside — only its events show.
  // Global announcements (a manual broadcast with no leagueId) are world-wide, so
  // they surface in whichever league bell is open rather than being hidden.
  const { today, earlier, unread } = useMemo(() => {
    const list = (notifications ?? []).filter(
      (n) => n.data?.leagueId === leagueId || (n.type === 'ANNOUNCEMENT' && !n.data?.leagueId)
    );
    return {
      today: list.filter((n) => now - n.createdAt < DAY_MS),
      earlier: list.filter((n) => now - n.createdAt >= DAY_MS),
      unread: list.filter((n) => n.readAt == null)
    };
  }, [notifications, leagueId, now]);

  const routeFor = (n: Notification) => {
    impact('light');
    if (n.readAt == null) markRead.mutate([n.notificationId]);
    navigate(notificationRouteOrInbox(n.type, n.data ?? {}, leagueId));
  };

  const hasAny = today.length > 0 || earlier.length > 0;

  return (
    <AppShell>
      <Content width="wide">
        <div className="flex items-center justify-between pt-2 md:pt-5">
          <button
            type="button"
            onClick={() => {
              impact('light');
              navigate(-1);
            }}
            className="tap -ml-1 flex items-center gap-[2px] text-ink"
          >
            <ChevronLeft width={22} height={22} strokeWidth={2.4} />
            <span className="text-[16px] font-semibold tracking-snug">Back</span>
          </button>
          {unread.length > 0 && (
            <button
              type="button"
              onClick={() => {
                impact('selection');
                markRead.mutate(unread.map((n) => n.notificationId));
              }}
              className="tap text-[13px] font-bold text-accent-ink"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="pt-3">
          {leagueId && <LeagueSwitcher leagueId={leagueId} tab="notifications" size="sm" />}
          <h1 className="title mt-[10px] text-[30px] md:text-[40px]">Notifications</h1>
        </div>

        {isLoading ? (
          <Placeholder Icon={Bell} title="Loading…" body="Fetching your latest activity." />
        ) : !hasAny ? (
          <Placeholder
            Icon={Bell}
            title="You’re all caught up"
            body="Round results, live card updates, duels, and friend activity in this league show up here."
          />
        ) : (
          <div className="flex flex-col gap-6 pt-4">
            {today.length > 0 && (
              <Section label="Today">
                {today.map((n) => (
                  <NotificationCard key={n.notificationId} n={n} now={now} sport={sport} onTap={() => routeFor(n)} />
                ))}
              </Section>
            )}
            {earlier.length > 0 && (
              <Section label="Earlier">
                {earlier.map((n) => (
                  <NotificationCard key={n.notificationId} n={n} now={now} sport={sport} onTap={() => routeFor(n)} />
                ))}
              </Section>
            )}
          </div>
        )}
      </Content>
    </AppShell>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="pb-[10px] text-[11px] font-bold uppercase tracking-wide text-ink2">{label}</div>
      <div className="flex flex-col gap-[10px] md:grid md:grid-cols-2 md:items-start">{children}</div>
    </div>
  );
}

function NotificationCard({
  n,
  now,
  sport,
  onTap
}: {
  n: Notification;
  now: number;
  sport?: Sport;
  onTap: () => void;
}) {
  const unread = n.readAt == null;
  const isFriend = n.type === 'FRIEND_PASSED' && n.data?.actorName;
  const { Icon, ring } = iconFor(n.type);
  // Announcements carry their own destination + (optional) button label; show a CTA
  // only when there's somewhere to go. Everything else uses the type-fixed label.
  const cta =
    n.type === 'ANNOUNCEMENT'
      ? n.data?.path
        ? (n.data?.ctaLabel ?? 'See more')
        : null
      : notificationCtaLabel(n.type, sport);

  return (
    <button
      type="button"
      onClick={onTap}
      className={cn(
        'tap relative w-full overflow-hidden rounded-[20px] p-[15px] text-left transition-transform active:scale-[0.99]',
        unread
          ? 'bg-gradient-to-br from-[#1C1C24] to-[#0C0C10] shadow-card'
          : 'border border-black/5 bg-card shadow-tile'
      )}
    >
      {unread && (
        <span className="pointer-events-none absolute -right-[30px] -top-[30px] h-[120px] w-[120px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.22),transparent_70%)]" />
      )}
      <div className="relative flex items-start gap-[13px]">
        {isFriend ? (
          <Avatar name={n.data?.actorName} tone={0} size={42} shape="rounded" />
        ) : (
          <div className={cn('grid h-[42px] w-[42px] shrink-0 place-items-center rounded-tile', ring)}>
            <Icon width={22} height={22} strokeWidth={2.2} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className={cn('text-[14px] font-bold tracking-snug', unread ? 'text-white' : 'text-ink')}>
            {n.title}
          </div>
          <div className={cn('mt-[2px] text-[12px] font-medium leading-[1.4]', unread ? 'text-[#9A9AA4]' : 'text-ink2')}>
            {n.body}
          </div>
        </div>
        <span className={cn('shrink-0 whitespace-nowrap text-[11px] font-semibold', unread ? 'text-[#7E7E88]' : 'text-ink3')}>
          {ago(n.createdAt, now)}
        </span>
      </div>
      {cta && (
        <div
          className={cn(
            'relative mt-[13px] flex items-center justify-center gap-[7px] rounded-tile py-[11px]',
            unread ? 'bg-white/10' : 'bg-night'
          )}
        >
          <span className="text-[13px] font-extrabold tracking-snug text-white">{cta}</span>
          <ArrowRight width={15} height={15} strokeWidth={2.6} className="text-white" />
        </div>
      )}
    </button>
  );
}